import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL, getAuthHeaders } from '../../config/api';
import { formatFeeDisplay, formatProgramDisplay } from '../../utils/formatters';
import { 
 AcademicCapIcon, 
 MapPinIcon, 
 CurrencyDollarIcon,
 SparklesIcon,
 ArrowPathIcon,
 CheckCircleIcon,
 BuildingOfficeIcon
} from '@heroicons/react/24/outline';

const UniversityRecommendations = () => {
 const { isAuthenticated, isStudent, token } = useAuth();
 const [recommendations, setRecommendations] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);
 const [studentMarks, setStudentMarks] = useState(null);

 useEffect(() => {
 if (isAuthenticated && isStudent && token) {
 fetchRecommendations();
 } else {
 setLoading(false);
 }
 }, [isAuthenticated, isStudent, token]);

 const fetchRecommendations = async () => {
 try {
 setLoading(true);
 setError(null);

 const response = await fetch(`${API_BASE_URL}/recommendations`, {
 method: 'POST',
 headers: getAuthHeaders(token),
 body: JSON.stringify({})
 });

 if (response.ok) {
 const data = await response.json();
 setRecommendations(data.recommendations || []);
 setStudentMarks(data.student_marks);
 } else {
 const errorData = await response.json();
 if (response.status === 400) {
 // Student hasn't completed academic profile
 setError(errorData.message || 'Please complete your academic profile');
 } else {
 setError('Failed to load recommendations');
 }
 }
 } catch (err) {
 console.error('Error fetching recommendations:', err);
 setError('Failed to load recommendations');
 } finally {
 setLoading(false);
 }
 };

 // Don't show anything if not a student
 if (!isAuthenticated || !isStudent) {
 return null;
 }

 // Loading state
 if (loading) {
 return (
 <div className="bg-gradient-to-r from-primary-100 via-emerald-100/40 via-75% to-green-100/60 rounded-xl shadow-sm border border-primary-100 p-6 mb-8">
 <div className="flex items-center gap-2 mb-4">
 <SparklesIcon className="h-5 w-5 text-amber-500 fill-amber-400 animate-pulse shrink-0" />
 <h3 className="text-lg font-bold text-slate-900">Recommended For You</h3>
 </div>
 <div className="flex justify-center py-8">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
 </div>
 </div>
 );
 }

 // Error state (profile incomplete) - Don't show, ProfileCompletionProgress handles this
 if (error) {
 return null;
 }

 // No recommendations - Don't show anything, let ProfileCompletionProgress handle it
 if (recommendations.length === 0) {
 return null;
 }

 // Show recommendations
 const gridColsClass = recommendations.length === 1 
 ? 'md:grid-cols-1' 
 : recommendations.length === 2 
 ? 'md:grid-cols-2' 
 : 'md:grid-cols-3';

 const containerMaxWidthClass = recommendations.length === 1
 ? 'max-w-sm'
 : recommendations.length === 2
 ? 'max-w-2xl'
 : 'w-full';

 return (
 <div className="bg-gradient-to-r from-primary-100 via-emerald-100/40 via-75% to-green-100/60 rounded-xl border border-primary-100 p-6 mb-8 shadow-sm">
 {/* Header */}
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
 <div>
 <h3 className="text-xl font-extrabold text-slate-900 flex items-center gap-2">
 <SparklesIcon className="h-5 w-5 text-amber-500 fill-amber-400 animate-pulse shrink-0" />
 Recommended For You
 </h3>
 <p className="text-sm text-slate-500 mt-1">Personalized matches based on your profile</p>
 </div>
 <div className="flex items-center gap-3 shrink-0 flex-wrap sm:flex-nowrap">
 {studentMarks && (
 <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full border border-primary-200 shadow-sm">
 <CheckCircleIcon className="h-4 w-4 text-green-600" />
 <span className="text-xs font-semibold text-slate-700">
 Your Marks: {Number(studentMarks).toFixed(2)}%
 </span>
 </div>
 )}
 <button
 onClick={fetchRecommendations}
 className="text-primary-600 hover:text-primary-700 text-sm font-semibold flex items-center gap-1 hover:underline"
 >
 Refresh Recommendations
 <ArrowPathIcon className="h-4 w-4" />
 </button>
 </div>
 </div>

 {/* Dynamic layout: Horizontal scroll on mobile, Grid on desktop */}
 <div className={`flex md:grid ${gridColsClass} ${containerMaxWidthClass} gap-5 overflow-x-auto md:overflow-x-visible pb-4 md:pb-0 scrollbar-hide snap-x snap-mandatory`}>
 {recommendations.map((rec, index) => {
 const logoUrl = rec.logo ? (rec.logo.startsWith('/static/') ? `${API_BASE_URL.replace('/api', '')}${rec.logo}` : rec.logo) : '';
 return (
 <div
 key={`${rec.university_type}-${rec.university_id}-${rec.program_id}`}
 className="flex-shrink-0 w-[290px] sm:w-[320px] md:w-full snap-start border border-primary-100 rounded-xl p-4 hover:shadow-lg hover:border-primary-300 transition-all duration-300 flex flex-col justify-between bg-white relative overflow-hidden group"
 >
 {/* Top Accent Line */}

 {/* Badges Container */}
 <div className="flex justify-between items-center gap-2 mb-3 mt-1.5">
 {/* Rank Badge */}
 <div className="flex items-center justify-center h-6 px-2 bg-primary-600 text-white text-xs font-bold rounded shadow-sm">
 #{index + 1}
 </div>

 {/* Preference Match Badge */}
 {rec.matches_preference && (
 <div className="px-2 py-0.5 bg-green-50 text-green-700 border border-green-200 rounded text-[10px] font-semibold tracking-wide uppercase">
 Matches Interest
 </div>
 )}
 </div>

 <div>
 {/* Header: Logo and Name/City side-by-side */}
 <div className="flex items-center gap-3 mb-3">
 <div className="w-12 h-12 rounded-lg border border-slate-100 overflow-hidden flex-shrink-0 flex items-center justify-center bg-slate-50">
 {logoUrl ? (
 <img
 src={logoUrl}
 alt={rec.university_name}
 className="w-full h-full object-cover"
 onError={(e) => {
 e.target.style.display = 'none';
 }}
 />
 ) : (
 <BuildingOfficeIcon className="h-6 w-6 text-slate-400" />
 )}
 </div>
 <div className="min-w-0 flex-1">
 <h4 className="text-sm font-bold text-slate-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
 {rec.university_name}
 </h4>
 <span className="text-xs text-slate-600 font-medium truncate block mt-0.5">
 {rec.location}
 </span>
 </div>
 </div>

 {/* Program Info */}
 <div className="mb-3">
 <p className="text-xs text-primary-600 font-semibold line-clamp-1 bg-primary-50 px-2 py-1 rounded w-fit">
 {formatProgramDisplay(rec.program_name, rec.degree_type)}
 </p>
 </div>

 {/* Details */}
 <div className="space-y-1.5 mb-3 border-t border-slate-100 pt-3">
 {/* Merit Cutoff */}
 <div className="flex items-center text-xs text-slate-700">
 <AcademicCapIcon className="h-4 w-4 mr-1.5 text-slate-400 flex-shrink-0" />
 <span>
 Merit: <span className="font-bold text-slate-900">{rec.cutoff_min}%{rec.cutoff_max && ` - ${rec.cutoff_max}%`}</span>
 </span>
 </div>

 {/* Fee */}
 {rec.program_fee && (
 <div className="flex items-center text-xs text-slate-700">
 <CurrencyDollarIcon className="h-4 w-4 mr-1.5 text-slate-400 flex-shrink-0" />
 <span>
 Tuition Fee: <span className="font-semibold text-slate-900">{formatFeeDisplay(rec.program_fee, rec.fee_type || 'semester')}</span>
 </span>
 </div>
 )}
 </div>
 </div>

 {/* Actions */}
 <div className="mt-4 pt-3 border-t border-slate-100">
 <Link
 to={`/university/${rec.university_id}${rec.university_type === 'static' ? '?type=static' : ''}`}
 className="block w-full text-center py-2 px-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs font-semibold transition-colors shadow-sm"
 >
 {rec.can_apply ? 'View & Apply' : 'View Details'}
 </Link>
 </div>
 </div>
 );
 })}
 </div>
 </div>
 );
};

export default UniversityRecommendations;
